import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminGetServiceCenter,
  superadminGetServiceCenterStats,
  superadminImpersonateServiceCenter,
} from "@pro-sto/api-client";
import type {
  SuperadminServiceCenterDetailDto as ServiceCenterDetail,
  ScStatsResponseDto as ServiceCenterStats,
} from "@pro-sto/api-client";
import type { ServiceCenterDetailExtended } from "./sdk-extended";
import { toastStore } from "../ToastStore";
import { TransactionsSubStore } from "./TransactionsSubStore";
import { BookingsSubStore } from "./BookingsSubStore";
import { PayoutsSubStore } from "./PayoutsSubStore";
import { PaymentDetailSubStore } from "./PaymentDetailSubStore";
import { LegalDataSubStore } from "./legal-store/LegalDataSubStore";

class ContractorDetailStore {
  // ─── Header ───
  contractor: ServiceCenterDetail | null = null;
  isLoading = false;
  isImpersonating = false;

  // ─── Stats ───
  stats: ServiceCenterStats | null = null;
  statsYear = new Date().getFullYear();
  isStatsLoading = false;

  // ─── Sub-stores ───
  tx: TransactionsSubStore;
  bk: BookingsSubStore;
  po: PayoutsSubStore;
  pd: PaymentDetailSubStore;
  legal: LegalDataSubStore;

  constructor() {
    // Инициализируем sub-stores ДО makeAutoObservable
    const getUuid = () => this.uuid;
    this.tx = new TransactionsSubStore(getUuid);
    this.bk = new BookingsSubStore(getUuid);
    this.po = new PayoutsSubStore(getUuid);
    this.pd = new PaymentDetailSubStore();
    this.legal = new LegalDataSubStore(getUuid);
    // Sub-stores уже реактивны сами по себе — исключаем из обзервации
    makeAutoObservable(this, {
      tx: false,
      bk: false,
      po: false,
      pd: false,
      legal: false,
    });
  }

  // ─── Getters ───

  get uuid(): string {
    return this.contractor?.uuid ?? "";
  }

  // ─── Header ───

  loadContractor = async (uuid: string) => {
    this.isLoading = true;
    // Сразу выставляем флаг проверки legal-данных, чтобы UI
    // не успел мигнуть NoLegalDataCard'ом до завершения checkLegalData()
    this.legal.status.isLoadingCheck = true;
    try {
      const data = await superadminGetServiceCenter({ uuid });
      runInAction(() => {
        this.contractor = data as ServiceCenterDetail;
        this.legal.status.isLegalDataFilled =
          (data as ServiceCenterDetailExtended).isLegalDataFilled ?? false;
        this.isLoading = false;
      });
      this.legal.checkLegalData();
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить данные контрагента");
    }
  };

  openServiceCenterPanel = async () => {
    if (!this.uuid || this.isImpersonating) return;

    runInAction(() => {
      this.isImpersonating = true;
    });

    try {
      const { code } = await superadminImpersonateServiceCenter({
        uuid: this.uuid,
      });
      const baseUrl = (
        import.meta.env.VITE_SERVICE_ADMIN_URL || "http://localhost:3005"
      ).replace(/\/$/, "");
      const url = `${baseUrl}/auth/impersonate?code=${encodeURIComponent(code)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toastStore.showError("Не удалось открыть панель сервисного центра");
    } finally {
      runInAction(() => {
        this.isImpersonating = false;
      });
    }
  };

  // ─── Stats ───

  setStatsYear = (year: number) => {
    const currentYear = new Date().getFullYear();
    if (year > currentYear) return;
    this.statsYear = year;
    if (this.uuid) this.loadStats();
  };

  loadStats = async (uuidOverride?: string) => {
    const id = uuidOverride ?? this.uuid;
    if (!id) return;
    this.isStatsLoading = true;
    try {
      const data = await superadminGetServiceCenterStats({
        uuid: id,
        year: this.statsYear,
      });
      runInAction(() => {
        this.stats = data;
        this.isStatsLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isStatsLoading = false;
      });
      toastStore.showError("Не удалось загрузить метрики");
    }
  };

  // ─── Reset ───

  reset = () => {
    this.contractor = null;
    this.stats = null;
    this.statsYear = new Date().getFullYear();
    this.isImpersonating = false;
    this.tx.reset();
    this.bk.reset();
    this.po.reset();
    this.pd.reset();
    this.legal.reset();
  };
}

export const contractorDetailStore = new ContractorDetailStore();
export type { ContractorDetailStore };

// Re-export types for consumers
export type {
  ServiceCenterDetail,
  StatsChart,
  ServiceBreakdown,
  ServiceCenterStats,
  TransactionItem,
  BookingItem,
  PayoutItem,
  PaginationMeta,
  PaymentDetail,
  PaymentEvent,
} from "./types";
